import React from 'react';
import LegalLayout from './LegalLayout';
import useSiteSettings from '../hooks/useSiteSettings';

const PrivacyPolicyPage = () => {
  const s = useSiteSettings();
  return (
  <LegalLayout title="Privacy Policy" lastUpdated="February 03, 2026" testId="privacy-policy-page">
    <p>
      <strong>BeeWeddy</strong> ("we", "us", "our") operates the BeeWeddy platform (web app and Android app — together the "Service"). This Privacy Policy
      explains what personal data we collect, why we collect it, and how we use, share, retain and protect it. We comply with India's
      Digital Personal Data Protection Act, 2023 (DPDP Act) and Google Play's Data Safety requirements.
    </p>

    <h2>1. Information We Collect</h2>

    <h3>1.1 Information you provide</h3>
    <ul>
      <li><strong>Account:</strong> name, phone number, role (customer / vendor / professional / captain).</li>
      <li><strong>Vendor / Professional profile:</strong> business name, category, description, base location, photos of work, package details, pricing.</li>
      <li><strong>Booking & Pricing:</strong> event type & date, guest count, event location, package items.</li>
      <li><strong>Wallet & Referrals:</strong> referral code used, wallet balance, withdrawal request details.</li>
      <li><strong>Captain / Event attendance:</strong> venue GPS coordinates set by the captain (used only for geofence check-in).</li>
    </ul>

    <h3>1.2 Information collected automatically</h3>
    <ul>
      <li><strong>Device & technical:</strong> IP address, browser type, OS, app version (used for security and crash diagnostics).</li>
      <li><strong>Cookies / local storage:</strong> session tokens, language preference, saved package previews.</li>
      <li><strong>Geolocation</strong> (one-time, opt-in): only when a professional taps "Check In" at an event venue, to verify they are within the geofence radius set by the captain.</li>
    </ul>

    <h3>1.3 We do <em>not</em> collect</h3>
    <ul>
      <li>Contacts list, SMS, call logs, microphone, or background location.</li>
      <li>Health, financial-account, or government-ID data (no PAN / Aadhaar collected within the app today).</li>
      <li>Data from children under 13. The Service is intended for users aged 18 and above.</li>
    </ul>

    <h2>2. How We Use Your Data</h2>
    <ul>
      <li>To create and manage your account, authenticate logins, and prevent fraud.</li>
      <li>To match customers with vendors and to compute Instant Pricing estimates.</li>
      <li>To process bookings, package submissions, captain assignments, and attendance.</li>
      <li>To credit invite bonuses, calculate platform commission, and process wallet withdrawals.</li>
      <li>To respond to support, refund, and dispute queries.</li>
      <li>To improve the Service (anonymized analytics) and to comply with legal obligations.</li>
    </ul>

    <h2>3. Legal Basis (DPDP Act 2023)</h2>
    <p>
      We process personal data based on (a) your <strong>consent</strong> obtained at registration and at sensitive events (location prompt,
      photo upload), and (b) <strong>legitimate uses</strong> such as performing the Service you signed up for, complying with law,
      and preventing harm.
    </p>

    <h2>4. Sharing & Disclosure</h2>
    <ul>
      <li><strong>Other users:</strong> vendor profiles, work galleries, and package listings are visible to logged-in customers. Customer name & event location are visible only to the assigned vendor / captain after a booking is confirmed.</li>
      <li><strong>Service providers:</strong> infrastructure (Google Cloud / AWS), database (MongoDB Atlas), email/SMS gateways. They process data only on our instructions.</li>
      <li><strong>Legal:</strong> when required by law, court order, or to protect our rights or user safety.</li>
      <li><strong>We do not sell or rent personal data to advertisers.</strong></li>
    </ul>

    <h2>5. Data Retention</h2>
    <ul>
      <li>Active account data is retained for the life of the account.</li>
      <li>On deletion (in-app or via public web request), we delete personal data within <strong>30 days</strong>, except where law requires longer retention (e.g., tax / financial records up to 7 years).</li>
      <li>Anonymized / aggregated analytics may be kept indefinitely.</li>
    </ul>

    <h2>6. Your Rights (DPDP Act §11–14)</h2>
    <ul>
      <li><strong>Right to access:</strong> download a JSON copy of your data via <em>Dashboard → Settings → Download My Data</em>.</li>
      <li><strong>Right to correction:</strong> edit profile fields anytime from your dashboard.</li>
      <li><strong>Right to erasure:</strong> delete your account in-app via <em>Settings → Delete My Account</em>, or use the public form at <a href="/delete-my-account">/delete-my-account</a>.</li>
      <li><strong>Right to grievance:</strong> contact our Grievance Officer (below); we respond within 30 days.</li>
      <li><strong>Right to nominate</strong> someone to exercise your rights in case of death/incapacity (DPDP §14).</li>
    </ul>

    <h2>7. Security</h2>
    <p>
      All traffic is encrypted in transit using HTTPS/TLS. Passwords (admin only) are hashed with bcrypt. Sessions use HttpOnly
      cookies. Phone-OTP login is being rolled out; until then, please do not share your phone number publicly. Despite our efforts,
      no online service can be 100% secure — please report suspected incidents immediately.
    </p>

    <h2>8. Children</h2>
    <p>
      The Service is for users <strong>18 years and above</strong>. Birthday events are planned by adult organizers; we do not
      knowingly collect data from minors. If you believe a minor has registered, contact us so we can remove the account.
    </p>

    <h2>9. International Transfers</h2>
    <p>
      Data is primarily stored in India. If we use service providers in other regions, transfers will comply with the DPDP Act
      cross-border-transfer rules notified by the Government of India.
    </p>

    <h2>10. Updates to this Policy</h2>
    <p>
      We may update this policy. Material changes will be announced in-app at least 7 days in advance. The "Last updated" date at
      the top reflects the current version. Continued use after the effective date means you accept the updated policy.
    </p>

    <h2>11. Grievance Officer</h2>
    <p>
      <strong>Grievance Officer:</strong> {s.company_name} Compliance Team<br />
      <strong>Email:</strong> <a href={`mailto:${s.grievance_email}`} data-testid="privacy-grievance-email">{s.grievance_email}</a><br />
      <strong>Address:</strong> {s.address}<br />
      We acknowledge complaints within 24 hours and resolve within 30 days as required by Rule 5(9) of the IT Rules, 2021.
    </p>
  </LegalLayout>
  );
};

export default PrivacyPolicyPage;
